import { Auth } from '@aws-amplify/auth'
import { track } from '@/utils/telemetry'

export class HttpError extends Error {
  constructor (message, status, statusText, body = null) {
    super(message)
    this.name = 'HttpError'
    this.status = status
    this.statusText = statusText
    this.body = body
  }
}

// A brief network-interface change (ERR_NETWORK_CHANGED / "Failed to fetch" — e.g.
// a VPN reconnect or WiFi roam) drops whatever request is in flight. Without a
// retry, a 1-2s blip fails an entire AI generation. fetch() only rejects on a
// network failure or an abort, so once we exclude aborts and real HTTP errors
// (which we throw ourselves as HttpError), a rejection here is a transient network
// fault that is safe to retry. POSTs are idempotent for our purposes — a retried
// generation just returns a fresh result; the client applies only the one that
// succeeds.
export const HTTP_NETWORK_RETRIES = 2
const RETRY_BASE_MS = 600

export const sendHttpRequest = async (url, method, headers, body, signal) => {
  let attempt = 0
  while (true) {
    try {
      const response = await fetch(url, { method, headers, body, signal })

      if (!response.ok) {
        const errorBodyText = await response.text()

        throw new HttpError(
          `HTTP Error: ${response.status} ${response.statusText}`,
          response.status,
          response.statusText,
          errorBodyText
        )
      }

      return response
    } catch (error) {
      // A real HTTP error or a deliberate abort is not retriable.
      if (error instanceof HttpError || error.name === 'AbortError') {
        throw error
      }

      // Transient network failure — retry a couple of times with a short backoff,
      // unless the caller has since aborted.
      attempt++
      if (attempt <= HTTP_NETWORK_RETRIES && !signal?.aborted) {
        // Telemetry so we can see how often a blip is auto-recovered vs. exhausted.
        track('http_retry', { attempt, message: String(error?.message || '').slice(0, 160) })
        await new Promise((resolve) => setTimeout(resolve, RETRY_BASE_MS * attempt))
        continue
      }

      track('http_retry_exhausted', { attempts: attempt, message: String(error?.message || '').slice(0, 160) })
      throw new Error(`Network request failed: ${error.message}`)
    }
  }
}

// Resolve the current Cognito id-token JWT for an AI/chat call. Auth.currentSession()
// auto-refreshes an expired token; forceRefresh additionally renews it explicitly,
// for the case where the server rejected a token the client still considered valid
// (it expired in flight on a long generation, or clock skew).
const getChatToken = async (forceRefresh = false) => {
  if (forceRefresh) {
    try {
      const user = await Auth.currentAuthenticatedUser()
      await new Promise((resolve, reject) =>
        user.refreshSession(user.signInUserSession.getRefreshToken(), (err, session) =>
          err ? reject(err) : resolve(session)))
    } catch (e) {
      // Fall through: currentSession() below still refreshes an expired token.
    }
  }
  const session = await Auth.currentSession()
  return session.getIdToken().getJwtToken()
}

// Send an authenticated chat/AI request, retrying ONCE on a 401 with a freshly
// refreshed token. A long, multi-step generation can outlive the 5-minute access
// token, so a later step's token expires in flight → the Lambda returns 401. Without
// this the step fails outright (and on the GraphQL path a 401 even logs the user out).
// Returns the fetch Response so the caller can stream/parse it as before.
export const sendAuthedChatRequest = async (url, requestBody, signal) => {
  let token
  try {
    token = await getChatToken(false)
  } catch (authError) {
    throw new Error('User authentication failed. Cannot call AI service.')
  }
  const headers = () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'text/plain;charset=UTF-8' })
  const body = JSON.stringify(requestBody)

  try {
    return await sendHttpRequest(url, 'POST', headers(), body, signal)
  } catch (error) {
    if (error instanceof HttpError && error.status === 401 && !signal?.aborted) {
      try {
        token = await getChatToken(true)
      } catch (refreshError) {
        throw error // refresh failed — surface the original 401
      }
      track('chat_auth_retry', {})
      return await sendHttpRequest(url, 'POST', headers(), body, signal)
    }
    throw error
  }
}
